import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trophy, ArrowRight, RotateCcw, CheckCircle, Save, Church, Sparkles } from 'lucide-react';
import { getGameProgress, getLeaderboard, getMe } from '../utils/api';
import confetti from 'canvas-confetti';

const MILESTONES = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500]; // Win condition is 5000

export default function LevelComplete({ user, refreshUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { level, score, membersGained } = location.state || {};
  const [allLevelsComplete, setAllLevelsComplete] = useState(false);
  const [showSaveProgressModal, setShowSaveProgressModal] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [userRank, setUserRank] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneMember, setMilestoneMember] = useState(0);
  const confettiRef = useRef(null);
  const [show3000Win, setShow3000Win] = useState(false);
  const [showYouLose, setShowYouLose] = useState(false);
  const [show2000Lock, setShow2000Lock] = useState(false); // New: Show when free levels lock at 2000
  const [show5000Mega, setShow5000Mega] = useState(false); // 5000 members: Mega Status
  const [show10000Pastor, setShow10000Pastor] = useState(false); // 10000 members: Great Mega Pastor
  const [show15000Win, setShow15000Win] = useState(false); // 15000 members: Final Win
  const [showGuestSignup, setShowGuestSignup] = useState(false); // Guest signup prompt after Level 1
  const [hasMilestone, setHasMilestone] = useState(false); // Track if milestone exists
  const [pendingAds, setPendingAds] = useState({ interstitial: false }); // Track pending ads
  // Store the committed game state values from backend
  const [committedMembersGained, setCommittedMembersGained] = useState(membersGained);
  const [committedTotalMembers, setCommittedTotalMembers] = useState(user?.total_members || 0);

  useEffect(() => {
    const guestMode = localStorage.getItem('guest_mode') === 'true';
    setIsGuestMode(guestMode && !user);

    // Guest user completed Level 1 - show signup prompt
    if (guestMode && !user && level === 1) {
      setTimeout(() => {
        setShowGuestSignup(true);
      }, 2000);
      return; // Don't show save progress modal for Level 1 guest
    }

    if (guestMode && !user) {
      setShowSaveProgressModal(true);
    }

    const initializeLevel = async () => {
      if (user) {
        // Fetch fresh user data DIRECTLY from backend to get the committed state
        // This ensures we have the latest total_members after level completion
        let freshTotal = user.total_members || 0;
        try {
          const response = await getMe();
          const freshUserData = response.data;
          
          // Now we have the fresh data with updated total_members
          freshTotal = freshUserData.total_members || 0;
          
          // CRITICAL DEBUG LOGGING
          console.log('=== LEVEL COMPLETE DEBUG ===');
          console.log('Level:', level);
          console.log('Members Gained (from navigation):', membersGained);
          console.log('Fresh Total Members (from backend):', freshTotal);
          console.log('User Total Members (before refresh):', user.total_members);
          console.log('============================');
          
          // Use membersGained from navigation state (what was committed)
          setCommittedMembersGained(membersGained || 0);
          setCommittedTotalMembers(freshTotal);
          
          // Also refresh the parent's user state for consistency
          if (refreshUser) {
            refreshUser();
          }
        } catch (error) {
          console.error('Failed to fetch fresh user data:', error);
          // Fallback to current user data
          setCommittedMembersGained(membersGained || 0);
          setCommittedTotalMembers(user.total_members || 0);
        }
        
        checkAllLevelsComplete();
        loadUserRanking();
        
        // NEW TIERED ACHIEVEMENT SYSTEM:
        // Check for 15,000 members - Final Win!
        if (freshTotal >= 15000) {
          setTimeout(() => {
            setShow15000Win(true);
          }, 1000);
          return; // Skip milestone and ad logic
        }
        
        // Check for 10,000 members - Great Mega Pastor
        if (freshTotal >= 10000) {
          setTimeout(() => {
            setShow10000Pastor(true);
          }, 1000);
          return; // Skip milestone and ad logic
        }
        
        // Check for 5,000 members - Mega Status
        if (freshTotal >= 5000) {
          setTimeout(() => {
            setShow5000Mega(true);
          }, 1000);
          return; // Skip milestone and ad logic
        }
        
        // Check if free levels (1-3) should be locked at 2000 members
        if (level >= 1 && level <= 3 && freshTotal >= 2000 && !user.is_premium) {
          // User reached 2000 members on free levels - show upgrade or reset option
          setTimeout(() => {
            setShow2000Lock(true);
          }, 1000);
          return; // Skip milestone and ad logic
        }
        
        // Check if there's a milestone to show
        const milestoneExists = checkForMilestone();
        
        if (milestoneExists) {
          // Milestone exists - show it first, queue ads for later
          setHasMilestone(true);
          setPendingAds({
            interstitial: level === 2 || level === 3 // Levels 2-3 get interstitial (Level 1 removed)
          });
          // Show milestone immediately
          setTimeout(() => setShowMilestone(true), 1000);
        } else {
        }
      }
    };

    initializeLevel();
  }, [user, level]);

  // Trigger confetti when 10000 members modal opens
  useEffect(() => {
    if (show10000Pastor) {
      triggerWinConfetti();
    }
  }, [show10000Pastor]);

  const checkForMilestone = () => {
    if (!user) return false;
    
    const previousTotal = user.total_members || 0;
    const newTotal = previousTotal + (membersGained || 0);
    
    // Check if we crossed a milestone
    const crossedMilestone = MILESTONES.find(milestone => 
      previousTotal < milestone && newTotal >= milestone
    );
    
    if (crossedMilestone) {
      // Get milestones already shown
      const shownMilestones = JSON.parse(localStorage.getItem('shown_milestones') || '[]');
      
      // Only show if not already shown
      if (!shownMilestones.includes(crossedMilestone)) {
        setMilestoneMember(crossedMilestone);
        triggerConfetti();
        
        // Mark as shown
        shownMilestones.push(crossedMilestone);
        localStorage.setItem('shown_milestones', JSON.stringify(shownMilestones));
        
        return true;
      }
    }
    
    return false;
  };

  const triggerConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#4169E1', '#9370DB']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#4169E1', '#9370DB']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  };

  const triggerWinConfetti = () => {
    // Smaller single burst that animates for 3 seconds
    const duration = 3000; // 3 seconds
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      colors: ['#FFD700', '#4169E1', '#9370DB', '#FF69B4', '#00FF00']
    };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 20 * (timeLeft / duration); // Gradually decrease particles

      // Fire from slightly left of center and slightly right of center
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const checkAllLevelsComplete = async () => {
    try {
      const response = await getGameProgress();
      const progress = response.data.progress || [];
      
      const completedLevels = progress.filter(p => p.completed).map(p => p.level);
      const allComplete = [1, 2, 3, 4, 5, 6].every(l => completedLevels.includes(l));
      
      setAllLevelsComplete(allComplete);
    } catch (error) {
      console.error('Failed to check progress:', error);
    }
  };

  const loadUserRanking = async () => {
    try {
      const response = await getLeaderboard();
      const leaderboard = response.data.leaderboard || [];
      setTotalPlayers(leaderboard.length);
      
      // Find user's rank
      const userIndex = leaderboard.findIndex(player => player.email === user.email);
      if (userIndex !== -1) {
        setUserRank(userIndex + 1);
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  // Removed checkForLoss function - no longer needed with new game logic

  const handleNextLevel = () => {
    setShowSaveProgressModal(false);
    if (allLevelsComplete) {
      navigate('/win');
    } else if (level < 6) {
      navigate(`/gameplay/${level + 1}`);
    } else {
      navigate('/level-select');
    }
  };

  const handleReplay = () => {
    setShowSaveProgressModal(false);
    navigate(`/gameplay/${level}`);
  };

  const handleSaveProgress = () => {
    navigate('/auth');
  };

  const handleContinueAsGuest = () => {
    setShowSaveProgressModal(false);
  };

  // Handler for "Keep Building" buttons at 5000 and 10000
  const handleKeepBuilding = () => {
    // Close the modal and return to level select
    setShow5000Mega(false);
    setShow10000Pastor(false);
    navigate('/level-select');
  };

  // Handler for "Start Over" button at 15000
  const handleStartOver = async () => {
    try {
      const { resetGame } = await import('../utils/api');
      await resetGame(false); // Completely reset everything to 0
      if (refreshUser) {
        await refreshUser();
      }
      navigate('/'); // Go to home page
    } catch (error) {
      console.error('Failed to reset game:', error);
    }
  };

  const handle3000WinPlayAgain = async () => {
    // Reset game levels but KEEP total_members for leaderboard
    // User can play again and add MORE members to their existing total
    try {
      const { resetGame } = await import('../utils/api');
      await resetGame(true); // keep_high_score = true to preserve total_members on leaderboard
      if (refreshUser) {
        await refreshUser(); // Refresh user data
      }
      navigate('/'); // Navigate to home page
    } catch (error) {
      console.error('Failed to reset game:', error);
      navigate('/');
    }
  };

  const handleYouLoseTryAgain = async () => {
    // Reset game score but keep user name/account
    try {
      const { resetGame } = await import('../utils/api');
      await resetGame(false); // keep_high_score = false to reset score
      if (refreshUser) {
        await refreshUser(); // Refresh user data to show 0 members
      }
      navigate('/level-select');
    } catch (error) {
      console.error('Failed to reset game:', error);
      navigate('/level-select');
    }
  };

  const handleMilestoneClose = () => {
    setShowMilestone(false);
    navigate('/level-select');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 relative"
      data-testid="level-complete-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      <Card className="max-w-2xl w-full bg-white/95 backdrop-blur-lg rounded-3xl p-12 text-center shadow-2xl relative z-10">
        {isGuestMode && (
          <div className="flex justify-center mb-4">
            <span className="bg-gray-200 px-4 py-2 rounded-full text-sm font-semibold text-gray-700">
              👤 Guest Mode
            </span>
          </div>
        )}

        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 p-6 rounded-full">
            <CheckCircle className="w-20 h-20 text-white" />
          </div>
        </div>

        <h1 className="text-5xl font-bold text-[#4169E1] mb-4" data-testid="complete-title">Level Complete!</h1>
        <p className="text-2xl text-gray-700 mb-8">Congratulations on finishing Level {level}!</p>

        <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-2xl p-8 mb-8">
          <div className="flex justify-center mb-4">
            <Trophy className="w-16 h-16 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" data-testid="members-gained-display">Members Gained</h2>
          <div className="text-6xl font-bold text-white mb-2">+{committedMembersGained}</div>
          <p className="text-xl text-white/90">Score: {score}</p>
        </div>

        {isGuestMode && (
          <div className="mb-8 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
            <p className="text-gray-700 font-semibold">
              ⚠️ Guest progress is not saved. Login to keep your {membersGained} members!
            </p>
          </div>
        )}

        {user && userRank && (
          <div className="mb-8 p-4 bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] rounded-xl shadow-lg">
            <div className="flex items-center justify-center gap-3">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <p className="text-xl text-white font-bold">
                Leaderboard Rank: #{userRank} of {totalPlayers}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-4 flex-col sm:flex-row">
          <Button
            data-testid="replay-btn"
            onClick={handleReplay}
            variant="outline"
            className="flex-1 h-16 text-lg font-bold border-2 border-[#4169E1] text-[#4169E1] hover:bg-[#4169E1] hover:text-white rounded-2xl"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Replay Level
          </Button>
          <Button
            data-testid="next-btn"
            onClick={handleNextLevel}
            className="flex-1 h-16 text-lg font-bold bg-[#4169E1] hover:bg-[#3558c7] rounded-2xl"
          >
            {allLevelsComplete ? 'Celebrate Victory!' : 'Next Level'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        <Button
          data-testid="back-levels-btn"
          onClick={() => navigate('/level-select')}
          variant="ghost"
          className="mt-6 text-gray-600"
        >
          Back to Level Select
        </Button>

        {/* Banner Ad */}
        <div className="mt-8">
        </div>
      </Card>

      {/* Milestone Celebration Modal */}
      <Dialog open={showMilestone} onOpenChange={(open) => {
        if (!open) handleMilestoneClose();
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md bg-gradient-to-br from-[#FFD700] via-[#FFA500] to-[#FFD700] border-4 border-white p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="text-3xl sm:text-4xl lg:text-5xl font-black text-white text-center mb-4 drop-shadow-lg">
              Great Job!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            {/* Celebration Icons */}
            <div className="flex justify-center gap-4 mb-6">
              <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-pulse" />
              <Church className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-pulse" />
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-4">
              <p className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {milestoneMember} Members! 🎉
              </p>
              <p className="text-xl sm:text-2xl font-semibold text-white/90">
                Your Church is Growing!
              </p>
            </div>

            {/* Motivational Message */}
            <p className="text-base sm:text-lg text-white/90 font-semibold mb-6">
              Keep going to reach Mega Church status!
            </p>

            {/* Continue Button */}
            <Button
              onClick={handleMilestoneClose}
              className="w-full h-14 text-xl font-bold bg-white text-[#4169E1] hover:bg-gray-100 rounded-full shadow-lg transform hover:scale-105 transition-all"
            >
              Continue Playing! 🚀
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Progress Modal */}
      <Dialog open={showSaveProgressModal} onOpenChange={setShowSaveProgressModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-3xl text-[#4169E1]">
              <Save className="w-8 h-8 text-[#4169E1]" />
              Save Your Progress?
            </DialogTitle>
            <DialogDescription className="text-lg pt-4 text-gray-700">
              Log in now to keep your members and rank on the leaderboard.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 mb-4">
              <p className="text-yellow-800 font-bold text-xl mb-2">You just earned +{membersGained} members!</p>
              <p className="text-yellow-700 text-base font-semibold">Don't lose your progress. Create an account to save it permanently.</p>
            </div>

            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-gray-800 font-semibold">
                <span className="text-green-500 font-bold text-xl">✓</span>
                Keep your {membersGained} members
              </li>
              <li className="flex items-center gap-2 text-gray-800 font-semibold">
                <span className="text-green-500 font-bold text-xl">✓</span>
                Save all level progress
              </li>
              <li className="flex items-center gap-2 text-gray-800 font-semibold">
                <span className="text-green-500 font-bold text-xl">✓</span>
                Compete on the leaderboard
              </li>
            </ul>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleContinueAsGuest}
              className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Continue as Guest
            </Button>
            <Button
              onClick={handleSaveProgress}
              className="w-full sm:w-auto bg-[#4169E1] hover:bg-[#3558c7] text-white font-bold"
            >
              Save Progress - Log In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 5000 Members - Mega Status Dialog */}
      <Dialog open={show5000Mega} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 border-4 border-yellow-400 p-8">
          <DialogHeader>
            <DialogTitle className="text-4xl sm:text-5xl font-black text-white text-center mb-4 drop-shadow-lg">
              You Have Reached Mega Status!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="text-8xl">🏆</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-2xl font-bold text-white mb-2">
                5,000 Members Achieved!
              </p>
              <p className="text-lg text-white/90">
                Your church is growing strong. Keep building!
              </p>
            </div>
            <Button
              onClick={handleKeepBuilding}
              className="w-full h-16 text-2xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black rounded-full shadow-xl"
            >
              ⚡ Keep Building
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 10000 Members - Great Mega Pastor Dialog */}
      <Dialog open={show10000Pastor} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl bg-gradient-to-br from-green-600 via-emerald-600 to-green-700 border-4 border-yellow-400 p-8">
          <DialogHeader>
            <DialogTitle className="text-4xl sm:text-5xl font-black text-white text-center mb-4 drop-shadow-lg">
              You Are a Great Mega Pastor!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="text-8xl">⭐</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-2xl font-bold text-white mb-2">
                10,000 Members Achieved!
              </p>
              <p className="text-lg text-white/90">
                Your leadership is inspiring. Continue your mission!
              </p>
            </div>
            <Button
              onClick={handleKeepBuilding}
              className="w-full h-16 text-2xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black rounded-full shadow-xl"
            >
              ⚡ Keep Building
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 15000 Members - Final Win Dialog */}
      <Dialog open={show15000Win} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl bg-black/95 backdrop-blur-lg border-4 border-yellow-400 p-6 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="sr-only">You Win Mega Church - 15,000 Members Achieved!</DialogTitle>
          </DialogHeader>
          <img 
            src="https://customer-assets.emergentagent.com/job_bibletrivia-2025/artifacts/zmu8ydle_Mega%20Church%20You%20WIN%202025.png"
            alt="You Win Mega Church"
            className="w-full h-auto rounded-lg mb-6"
          />
          <Button
            onClick={handleStartOver}
            className="w-full h-16 sm:h-20 text-2xl sm:text-3xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 text-white rounded-full shadow-2xl transform hover:scale-105 transition-all"
          >
            🔄 Start Over
          </Button>
        </DialogContent>
      </Dialog>

      {/* 2000 Members Lock Dialog - Upgrade or Reset */}
      <Dialog open={show2000Lock} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-md bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 border-4 border-yellow-400 p-8">
          <DialogHeader>
            <DialogTitle className="text-3xl sm:text-4xl font-black text-white text-center mb-4 drop-shadow-lg">
              Free Levels Completed!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="text-7xl">🔒</div>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-xl font-bold text-white mb-2">
                You've reached 2,000 members!
              </p>
              <p className="text-lg font-semibold text-white/90">
                Unlock Pro Levels 4-6 or start fresh
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/premium')}
                className="w-full h-14 text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black rounded-full shadow-lg"
              >
                🌟 Upgrade to Pro - $3
              </Button>
              <Button
                onClick={handleYouLoseTryAgain}
                variant="outline"
                className="w-full h-14 text-xl font-bold bg-white text-blue-700 hover:bg-gray-100 rounded-full"
              >
                🔄 Reset & Play Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest User - Sign Up Prompt after Level 1 */}
      <Dialog open={showGuestSignup} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] sm:max-w-md bg-gradient-to-br from-green-600 via-blue-600 to-green-700 border-4 border-yellow-400 p-8">
          <div className="text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="text-7xl">🎮</div>
            </div>

            {/* Main Message */}
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4 drop-shadow-lg">
              Great Job!
            </h2>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-xl font-bold text-white mb-2">
                You completed Level 1!
              </p>
              <p className="text-lg font-semibold text-white/90">
                Create a FREE account to unlock more levels and continue building your Mega Church!
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/auth')}
                className="w-full h-14 text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black rounded-full shadow-lg"
              >
                🌟 Create Free Account
              </Button>
              <Button
                onClick={() => {
                  setShowGuestSignup(false);
                  navigate('/level-select');
                }}
                variant="outline"
                className="w-full h-14 text-xl font-bold bg-white text-green-700 hover:bg-gray-100 rounded-full"
              >
                ↩️ Back to Levels
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
